import { supabase } from '../lib/supabase';

/**
 * Comprehensive real-time service for handling Supabase real-time subscriptions
 * Supports notifications, messages, conversations, reviews, and transactions
 */
export const realtimeService = {
  /**
   * Subscribe to real-time notifications for the current user
   * @param {string} userId - Current user ID
   * @param {Function} callback - Callback function to handle notification updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToNotifications(userId, callback) {
    const channel = supabase?.channel(`notifications:${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const convertedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? {
              id: payload?.new?.id,
              userId: payload?.new?.user_id,
              type: payload?.new?.type,
              title: payload?.new?.title,
              message: payload?.new?.message,
              isRead: payload?.new?.is_read,
              relatedId: payload?.new?.related_id,
              relatedType: payload?.new?.related_type,
              senderId: payload?.new?.sender_id,
              createdAt: payload?.new?.created_at,
              readAt: payload?.new?.read_at
            } : null,
            old: payload?.old ? {
              id: payload?.old?.id,
              userId: payload?.old?.user_id
            } : null
          };
          callback(convertedPayload);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  /**
   * Subscribe to real-time messages in a conversation
   * @param {string} conversationId - Conversation ID
   * @param {Function} callback - Callback function to handle message updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToMessages(conversationId, callback) {
    const channel = supabase?.channel(`messages:${conversationId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          const convertedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? {
              id: payload?.new?.id,
              conversationId: payload?.new?.conversation_id,
              senderId: payload?.new?.sender_id,
              content: payload?.new?.content,
              isRead: payload?.new?.is_read,
              createdAt: payload?.new?.created_at
            } : null,
            old: payload?.old ? {
              id: payload?.old?.id
            } : null
          };
          callback(convertedPayload);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  /**
   * Subscribe to real-time conversation updates for a user
   * @param {string} userId - Current user ID
   * @param {Function} callback - Callback function to handle conversation updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToConversations(userId, callback) {
    const channel = supabase?.channel(`conversations:${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `or(user1_id.eq.${userId},user2_id.eq.${userId})`
        },
        (payload) => {
          const convertedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? {
              id: payload?.new?.id,
              user1Id: payload?.new?.user1_id,
              user2Id: payload?.new?.user2_id,
              listingId: payload?.new?.listing_id,
              status: payload?.new?.status,
              lastMessageAt: payload?.new?.last_message_at,
              createdAt: payload?.new?.created_at,
              updatedAt: payload?.new?.updated_at
            } : null,
            old: payload?.old ? {
              id: payload?.old?.id
            } : null
          };
          callback(convertedPayload);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  /**
   * Subscribe to all messages for the current user (across all conversations)
   * @param {string} userId - Current user ID
   * @param {Function} callback - Callback function to handle message updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToUserMessages(userId, callback) {
    const channel = supabase?.channel(`user_messages:${userId}:${Date.now()}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          // Check if user is part of this conversation
          const { data: conversation } = await supabase?.from('conversations')?.select('user1_id, user2_id')?.eq('id', payload?.new?.conversation_id)?.single();

          if (conversation && 
              (conversation?.user1_id === userId || conversation?.user2_id === userId) &&
              payload?.new?.sender_id !== userId) {
            const convertedPayload = {
              eventType: 'INSERT',
              new: {
                id: payload?.new?.id,
                conversationId: payload?.new?.conversation_id,
                senderId: payload?.new?.sender_id,
                content: payload?.new?.content,
                isRead: payload?.new?.is_read,
                createdAt: payload?.new?.created_at
              }
            };
            callback(convertedPayload);
          }
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  /**
   * Subscribe to real-time reviews for a user
   * @param {string} userId - Current user ID
   * @param {Function} callback - Callback function to handle review updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToReviews(userId, callback) {
    const channel = supabase?.channel(`reviews:${userId}:${Date.now()}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reviews',
          filter: `reviewee_id=eq.${userId}`
        },
        (payload) => {
          const convertedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? {
              id: payload?.new?.id,
              transactionId: payload?.new?.transaction_id,
              reviewerId: payload?.new?.reviewer_id,
              revieweeId: payload?.new?.reviewee_id,
              rating: payload?.new?.rating,
              title: payload?.new?.title,
              comment: payload?.new?.comment,
              communicationRating: payload?.new?.communication_rating,
              conditionRating: payload?.new?.condition_rating,
              timelinessRating: payload?.new?.timeliness_rating,
              status: payload?.new?.status,
              isPublic: payload?.new?.is_public,
              isEdited: payload?.new?.is_edited,
              createdAt: payload?.new?.created_at,
              updatedAt: payload?.new?.updated_at
            } : null,
            old: payload?.old ? {
              id: payload?.old?.id
            } : null
          };
          callback(convertedPayload);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  /**
   * Subscribe to real-time transactions for a user
   * @param {string} userId - Current user ID
   * @param {Function} callback - Callback function to handle transaction updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToTransactions(userId, callback) {
    const channel = supabase?.channel(`transactions:${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions'
        },
        async (payload) => {
          // Check if user is involved in this transaction
          if (payload?.new?.initiator_id === userId || payload?.new?.responder_id === userId) {
            const convertedPayload = {
              eventType: payload?.eventType,
              new: {
                id: payload?.new?.id,
                initiatorId: payload?.new?.initiator_id,
                responderId: payload?.new?.responder_id,
                initiatorListingId: payload?.new?.initiator_listing_id,
                responderListingId: payload?.new?.responder_listing_id,
                status: payload?.new?.status,
                terms: payload?.new?.terms,
                description: payload?.new?.description,
                createdAt: payload?.new?.created_at,
                updatedAt: payload?.new?.updated_at,
                completedAt: payload?.new?.completed_at
              },
              old: payload?.old ? {
                id: payload?.old?.id,
                status: payload?.old?.status
              } : null
            };
            callback(convertedPayload);
          }
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  /**
   * Subscribe to multiple real-time channels at once
   * @param {string} userId - Current user ID
   * @param {Object} callbacks - Object containing callback functions for each channel
   * @returns {Function} Unsubscribe function for all channels
   */
  subscribeToMultiple(userId, callbacks = {}) {
    const unsubscribeFunctions = [];

    if (callbacks?.notifications) {
      unsubscribeFunctions?.push(
        this?.subscribeToNotifications(userId, callbacks?.notifications)
      );
    }

    if (callbacks?.messages) {
      unsubscribeFunctions?.push(
        this?.subscribeToUserMessages(userId, callbacks?.messages)
      );
    }

    if (callbacks?.reviews) {
      unsubscribeFunctions?.push(
        this?.subscribeToReviews(userId, callbacks?.reviews)
      );
    }

    if (callbacks?.transactions) {
      unsubscribeFunctions?.push(
        this?.subscribeToTransactions(userId, callbacks?.transactions)
      );
    }

    return () => {
      unsubscribeFunctions?.forEach(unsubscribe => unsubscribe());
    };
  }
};